import { Link } from "react-router";
import { Badge } from "../components/Badge";
import { Skeleton, SkeletonText } from "../components/Skeleton";
import { SPORT_LABEL, competitionLabel } from "../config/catalogue";
import { clubColours, teamColours } from "../config/teamColours";
import { fixturePagePath } from "../config/fixturePages";
import { isInProgress, orderSportsByNextStart } from "../features/fixture/landing";
import {
  formatDayHeading,
  formatKickoffTime,
  localDayKey,
} from "../features/fixture/format";
import { rowMeta } from "../features/fixture/rowMeta";
import type { PublicFixture, PublicFixtureValue } from "../services/api/schemas";
import styles from "./Home.module.css";

/**
 * The fixture list itself — grouped by sport, then by day — shared between
 * `Home.tsx` (the landing page, `selectLanding`'s fixtures) and
 * `AllFixtures.tsx` (`/fixtures`, `selectFullList`'s fixtures). Both pages
 * pass this component an already-windowed list; this component's only job is
 * to order and group WHAT IT IS GIVEN, never to decide how much of the feed
 * that should be — see `features/fixture/landing.ts` for that half.
 *
 * Moved out of Home.tsx 2026-09-07 so the "see more" page renders the exact
 * same row markup, grouping and styling rather than a second hand-built copy
 * that could drift from it — this is a windowing change, not a redesign.
 */
interface Props {
  fixtures: PublicFixture[];
  now: Date;
  /**
   * How many rows of the published file this client could not read — see the
   * per-row tolerance note in `services/api/schemas.ts`. Passed from the
   * RESPONSE, not from `fixtures`: a dropped row never reaches the window, so
   * the list itself cannot tell that anything is missing.
   */
  droppedCount?: number;
}

export function FixtureListSection({ fixtures, now, droppedCount = 0 }: Props) {
  const sportOrder = orderSportsByNextStart(fixtures, now);

  return (
    <>
      {sportOrder.map((sport) => (
        <div key={sport} className={styles.sportGroup}>
          {/* No heading at all when there is only one sport on the card —
              football, today — so the page does not carry a label nobody
              needs yet. It appears the moment a second sport does. */}
          {sportOrder.length > 1 && (
            <h2 className={styles.sportHeading}>{SPORT_LABEL[sport]}</h2>
          )}
          {groupByDay(fixtures.filter((f) => f.sport === sport)).map((group) => (
            <div key={group.key} className={styles.day}>
              <h3 className={styles.dayHeading}>{group.label}</h3>
              <ul className={styles.list}>
                {group.fixtures.map((f) => (
                  <FixtureRow key={f.key} fixture={f} now={now} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
      <DroppedNotice count={droppedCount} />
    </>
  );
}

/**
 * SAY THAT THE LIST IS SHORT, rather than quietly serving a thinned one.
 *
 * The per-row tolerance in `services/api/schemas.ts` exists so one unreadable
 * fixture costs one row instead of the whole page. The cost of that trade is
 * that a page which is missing something looks exactly like a page that is
 * complete — which is the same dishonesty in a smaller box. So the drop is
 * stated: how many, and that it is this client's failure to read them rather
 * than a fixture that does not exist.
 *
 * No key, no field name, no "contact support" — the reader can do nothing with
 * any of that. `console.warn` carries the detail for whoever can.
 */
function DroppedNotice({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <p className={styles.dropped} role="status">
      {count === 1
        ? "One more fixture came back unreadable, so it is not shown."
        : `${count} more fixtures came back unreadable, so they are not shown.`}
    </p>
  );
}

/**
 * The list's own shape while the feed loads — one flat run of rows, since
 * which sport or day they will fall under is exactly what has not arrived
 * yet. Six is a plausible landing-page count without over-committing to a
 * height the real (grouped, headed) list may not fill.
 */
export function FixtureListSkeleton() {
  return (
    <Skeleton label="the fixture list">
      <ul className={styles.list}>
        {Array.from({ length: 6 }).map((_, i) => (
          <li key={i} className={styles.row}>
            <span className={styles.rowMain}>
              <span className={styles.rowBody}>
                <span className={styles.league}>
                  <SkeletonText width="90px" />
                </span>
                <span className={styles.team}>
                  <SkeletonText width="60%" />
                </span>
                <span className={styles.team}>
                  <SkeletonText width="50%" />
                </span>
              </span>
              <span className={styles.rowTime}>
                <SkeletonText width="40px" />
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Skeleton>
  );
}

/**
 * Where a row's fixture page lives — `null` for a sport with no page built yet,
 * which `FixtureRow` renders as plain text rather than a dead link.
 *
 * ⚠️ THIS USED TO BE `if (f.sport === "football")`, AND THAT IS WHY NFL ROWS
 * WERE NOT CLICKABLE. The NFL exporter had been building sixteen fixture pages
 * a day for a week; every NFL row got the `null` branch and rendered as plain
 * text, which is not an error and so was reported by a person rather than by a
 * test. A second hardcoded branch would be the same bug with a longer body, so
 * the answer moved into `config/fixturePages.ts` — ONE table, read by this, by
 * the routes in App.tsx, by both fetch hooks and by upNav, so a sport cannot be
 * half-added. See that file's header for why the alternative (a `hasPage` flag
 * on the payload) was rejected.
 */
function hrefFor(f: PublicFixture): string | null {
  return fixturePagePath(f.sport, f.key);
}

/**
 * One fixture as a card, laid out after Google's match cards (Ned, 2026-09-13):
 * the competition where Google prints the season stage, the two clubs stacked
 * with their colours where Google has logos, and the kickoff alone on the right
 * past a divider.
 *
 * FULL CLUB NAMES AGAIN, NOT THE THREE-LETTER BUGS. The bugs (2026-09-10) existed
 * so a whole fixture fitted on one line beside its neighbours: "BHA v NFO" could
 * share a line where "Brighton & Hove Albion v Nottingham Forest" could not.
 * Stacked, each club has a line to itself, so the reason for the code is gone
 * and the name is the better label. A name too long for a narrow card is
 * ellipsised, with the full name on `title`.
 *
 * The "v" is still in the markup, visually hidden, so the link's accessible name
 * reads "Celta Vigo v Malaga" rather than two names run together.
 */
function FixtureRow({ fixture: f, now }: { fixture: PublicFixture; now: Date }) {
  const to = hrefFor(f);
  const content = (
    <>
      <span className={styles.rowBody}>
        <span className={styles.rowHead}>
          {/* The feed carries a SLUG ("seriea", "ligue1"); this is what the
              reader sees, so it is mapped. See competitionLabel. */}
          <span className={styles.league}>{competitionLabel(f.competition)}</span>
          <ValueChip value={f.value} />
        </span>
        <TeamLine club={f.home} sport={f.sport} />
        <span className="sr-only"> v </span>
        <TeamLine club={f.away} sport={f.sport} />
        <RowMeta fixture={f} />
      </span>
      <span className={styles.rowTime}>
        <span className={styles.kickoff}>{formatKickoffTime(f.kickoff)}</span>
        {/* Plainly labelled, and nothing more: "kicked off", never a score and
            never a "LIVE" claim — the payload has no status field to back
            either. See features/fixture/landing.ts's own note on the window
            this is read off. */}
        {isInProgress(f, now) && <Badge tone="neutral">Kicked off</Badge>}
      </span>
    </>
  );

  return (
    <li className={styles.row}>
      {to ? (
        <Link to={to} className={styles.rowMain}>
          {content}
        </Link>
      ) : (
        <span className={styles.rowMain}>{content}</span>
      )}
    </li>
  );
}

/**
 * A club's line on the card: its colours, then its name.
 *
 * The square stands where Google puts a logo. Colours stay decoration only, per
 * Late Night Cable's colour split — a club's colours are an external brand fact,
 * never a probability or a value verdict — so the square is `aria-hidden` and
 * the name beside it is the label. See config/teamColours.ts.
 *
 * A club we hold no colours for keeps the square's SPACE and draws nothing in
 * it, so the names down a column stay aligned; ClubBug.tsx carries the argument
 * for why reserved space is not the grey placeholder the swatch rule forbids.
 */
function TeamLine({ club, sport }: { club: string; sport: string }) {
  const colours = sport === "nba" ? teamColours(club) : clubColours(club);
  return (
    <span className={styles.team}>
      <span
        aria-hidden="true"
        className={colours ? styles.teamSwatch : styles.teamSwatchAbsent}
        style={
          colours
            ? {
                background: `linear-gradient(100deg, ${colours.primary} 0 62%, ${colours.secondary} 62% 100%)`,
              }
            : undefined
        }
      />
      <span className={styles.teamName} title={club}>
        {club}
      </span>
    </span>
  );
}

/**
 * The row's second line — venue, then each club's form. Both are FACTS ABOUT
 * THE FIXTURE, not Kev's read on it, which is why they are allowed on the
 * anonymous list at all; `features/fixture/rowMeta.ts` holds the tier argument
 * and, more importantly, the measurement of what each sport's row actually
 * carries. Short version: NFL rows have a venue and each side's last completed
 * season; football rows have neither, so football renders no second line and
 * nothing is invented to fill it.
 *
 * Its own line under the clubs, in the card's left column. Inside the
 * `<Link>`, not beside it: it contains no interactive element of its own,
 * and keeping it in there means the whole two-line row is one target and one
 * accessible name, read in the order it is printed.
 */
function RowMeta({ fixture }: { fixture: PublicFixture }) {
  const parts = rowMeta(fixture);
  if (parts.length === 0) return null;
  return (
    <span className={styles.rowMeta}>
      {parts.map((part, i) => (
        <span
          key={part.kind === "venue" ? part.venue : part.club}
          className={styles.rowMetaPart}
        >
          {/* A separator that is decoration, so it is not spoken as a word and
              not selected when the line is copied. */}
          {i > 0 && (
            <span className={styles.rowMetaSep} aria-hidden="true">
              ·
            </span>
          )}
          {part.kind === "venue" ? (
            part.venue
          ) : (
            <>
              {part.club}{" "}
              {/* NOWRAP, because the line wraps and the browser breaks inside
                  "11-6" at the hyphen — three of the sixteen published NFL rows
                  did exactly that at 375px, showing one record as two numbers on
                  two lines. See rowMeta.ts's note on why `record` is a separate
                  field rather than part of a sentence. */}
              <span className={styles.rowMetaRecord}>{part.record}</span>
              {part.season !== null && ` (${part.season})`}
            </>
          )}
        </span>
      ))}
    </span>
  );
}

/**
 * The value indicator — "the existence of an edge is public, its size is
 * not" (Ned, 2026-09-07). Three states, three DIFFERENT renders:
 *
 *   found: true   — the single word "Value", THE SAME FOR EVERY SUBSCRIPTION
 *                    LEVEL (Ned, 2026-09-07). It used to name the market(s),
 *                    which read as three different chips down one list —
 *                    "Value found", "Value: draw no bet", "Value: draw, draw
 *                    no bet" — and that is more than existence: a signed-out
 *                    reader could tell a one-market fixture from a two-market
 *                    one, and which market, without an account. The rule is
 *                    "the existence of an edge is public, its size is not",
 *                    and the market is part of the size. One word states the
 *                    fact and nothing else.
 *
 *                    It also retires a whole bug class. `markets` can be EMPTY
 *                    while `found` stays true (the exporter omits a label it
 *                    cannot reduce to a side-free name rather than leak a
 *                    side), and on 2026-09-07 Chelsea v Hull City published
 *                    `{found: true, markets: [], count: 1}` and rendered a
 *                    dangling "Value: " with nothing after it. A label that
 *                    never interpolates cannot dangle.
 *   found: false  — NOTHING, since 2026-09-07. There is no "no value" card;
 *                    only a value card (Ned).
 *   found: null   — nothing, as before.
 *
 * ── WHY false AND null MAY SHARE A RENDER, WHEN THEY ONCE COULD NOT ────────
 * kev-docs/kev-landing-and-seo-plan.md says "an absent verdict and a negative
 * verdict must never look the same", and this used to render `false` as a
 * plain "No value flagged" line for exactly that reason. That rule is about
 * not putting a NEGATIVE CLAIM where there is no fact — the failure it was
 * written against was `null` rendering as "no value flagged", which asserts
 * Kev looked when he had not.
 *
 * Rendering NEITHER makes no claim about either, so the rule is satisfied
 * rather than broken. What is genuinely given up is coverage: a reader can no
 * longer tell "checked, nothing found" from "not checked" at list level. That
 * distinction still exists in the payload and is still drawn on the fixture
 * page itself, which is where a reader who cares is going anyway. A list whose
 * job is to surface value should surface value, not narrate its own silence
 * on most rows.
 */
function ValueChip({ value }: { value: PublicFixtureValue }) {
  return value.found === true ? <Badge tone="signal">Value</Badge> : null;
}

interface DayGroup {
  key: string;
  label: string;
  fixtures: PublicFixture[];
}

/** Sorted by kickoff, then grouped into consecutive days — the caller has
    already decided which fixtures to show; this only ever REORGANISES that
    set for display, never re-derives which fixtures exist. */
function groupByDay(fixtures: PublicFixture[]): DayGroup[] {
  const sorted = [...fixtures].sort(
    (a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime(),
  );

  const groups: DayGroup[] = [];
  for (const f of sorted) {
    const key = localDayKey(f.kickoff);
    const last = groups.at(-1);
    if (last && last.key === key) {
      last.fixtures.push(f);
    } else {
      groups.push({ key, label: formatDayHeading(f.kickoff), fixtures: [f] });
    }
  }
  return groups;
}
